import {
  CSSProperties,
  ElementType,
  ReactElement,
  ReactNode,
  useMemo,
} from 'react';
import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  InputLabelProps as MUIInputLabelPropsType,
  InputProps as MUIInputPropsType,
  MenuProps as MUIMenuPropsType,
  OutlinedInput,
  Select as MUISelect,
  SelectProps as MUISelectPropsType,
  SxProps,
} from '@mui/material';

import { Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';

import MenuItem from './MenuItem';

type AtomMUIInputLabelProps = {
  style?: CSSProperties;
  classes?: any;
};

type AtomMUIInputProps = {
  style?: CSSProperties;
  classes?: any;
};

type AtomMUIMenuProps = {
  style?: CSSProperties;
  classes?: any;
};

type CustomProps = {
  /**
   * style to be applied to the input container
   */
  style?: CSSProperties;
  /**
   * Fix label to "floating" position (focused)
   * defaults to true
   */
  floatingLabelFixed?: boolean;
  /**
   * helperText to be applied under the Select
   */
  helperText?: string;
  label?: string;
  InputLabelProps?: AtomMUIInputLabelProps;
  InputProps?: AtomMUIInputProps;
  MenuProps?: AtomMUIMenuProps;
  fullWidth?: boolean;
  error?: boolean;
  inputLabelSx?: SxProps;
  /**
   * sx overrides merged into the helperText styles
   */
  helperTextSx?: SxProps;
  /**
   * sx overrides merged into the outlined input styles
   */
  inputSx?: SxProps;
  variant?: 'filled' | 'standard' | 'outlined';
};

type AtomMUISelectProps = {
  id?: string;
  multiple?: boolean;
  input?: ReactElement<any>;
  disabled?: boolean;
  loading?: boolean;
  IconComponent?: ElementType;
  children?: ReactNode;
  onChange?: (event: any) => void;
  value?: any;
  defaultValue?: any;
  renderValue?: (value: any) => ReactNode;
  displayEmpty?: boolean;
  classes?: any;
  disableUnderline?: boolean;
  placeholder?: string;
  /**
   * controls the menu, e.g. to confirm before it opens
   */
  open?: boolean;
  onOpen?: (event: any) => void;
  onClose?: (event: any) => void;
  'data-cy'?: string;
};

export type SelectProps = CustomProps & AtomMUISelectProps;

const getInputLabelShrink = ({
  floatingLabelFixed = true,
}: SelectProps): any => {
  return floatingLabelFixed
    ? { transform: 'unset', fontSize: fonts.sm }
    : {
        fontSize: fonts.sm,
        overflow: 'unset',
        textOverflow: 'unset',
        whiteSpace: 'unset',
      };
};

const defaultStyle: CSSProperties = {
  width: '100%',
};

export const Select = (props: SelectProps) => {
  const {
    style,
    helperText,
    multiple,
    input,
    disabled,
    loading = false,
    error = false,
    fullWidth = true,
    label = '',
    floatingLabelFixed = true,
    InputLabelProps = {},
    InputProps = {},
    MenuProps = {},
    variant = 'standard',
    IconComponent,
    inputLabelSx,
    helperTextSx,
    inputSx,
    ...data
  } = props;

  const MUISelectProps: MUISelectPropsType = data;
  const MUIInputProps: MUIInputPropsType = InputProps;
  const MUIInputLabelProps: MUIInputLabelPropsType = InputLabelProps;
  const MUIMenuProps: Partial<MUIMenuPropsType> = MenuProps;

  const getInputLabel = (): any => {
    return floatingLabelFixed
      ? { position: 'unset', color: colors.neutral.gray, ...inputLabelSx }
      : {
          color: colors.neutral.gray,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          bottom: '0px',
          maxWidth: '100%',
          ...inputLabelSx,
        };
  };
  const sxInputLabel: SxProps = useMemo(
    () => ({
      ...getInputLabel(),
      '&.MuiInputLabel-shrink': {
        ...getInputLabelShrink({ floatingLabelFixed }),
      },
      '&.Mui-focused': {
        color: `${colors.neutral.gray} !important`,
      },
      '&.Mui-error': {
        color: `${colors.utility.error} !important`,
      },
    }),
    [floatingLabelFixed],
  );

  const sxInput: SxProps = useMemo(
    () => ({
      fontSize: fonts.md,
      '&:before': {
        borderBottom: `1px solid ${colors.neutral.silver}`,
      },
      '&:after': {
        borderBottom: `1px solid ${colors.neutral.silver}`,
      },
      '&.MuiInput-root': {
        marginTop: '0',
      },
    }),
    [],
  );

  const sxOutlinedInput: SxProps = {
    fontSize: fonts.md,
    ...inputSx,
  };

  const getInput = () => {
    if (input) {
      return input;
    }

    return variant === 'outlined' ? (
      <OutlinedInput {...MUIInputProps} sx={sxOutlinedInput} />
    ) : (
      <Input {...MUIInputProps} sx={sxInput} />
    );
  };

  const sxHelperText: SxProps = {
    height: 0,
    marginTop: 0,
    ...helperTextSx,
  };

  return (
    <div style={{ ...defaultStyle, ...style }}>
      <FormControl fullWidth={fullWidth} error={error}>
        <InputLabel
          {...MUIInputLabelProps}
          variant={variant}
          sx={sxInputLabel}
          shrink={floatingLabelFixed}
        >
          {label}
        </InputLabel>
        <MUISelect
          {...MUISelectProps}
          multiple={multiple}
          disabled={disabled || loading}
          IconComponent={
            loading
              ? iconProps => <Progress {...iconProps} size={16} />
              : IconComponent
          }
          MenuProps={MUIMenuProps}
          input={getInput()}
        />
        {helperText && (
          <FormHelperText sx={sxHelperText}>{helperText}</FormHelperText>
        )}
      </FormControl>
    </div>
  );
};

Select.MenuItem = MenuItem;

export default Select;
